import { PossiblyReadonlyArray } from "../../../../api/dist/api/src/utils/types/Generics";
import { isPresent, presence, sift } from "../utils";

export type BucketedClasses = Record<
  | "padding"
  | "margin"
  | "size"
  | "flexGrid"
  | "layout"
  | "typography"
  | "color"
  | "border"
  | "effects"
  | "animate"
  | "spacing"
  | "divide"
  | "others",
  string[]
>;

export function bucketTailwindClasses(str: string): BucketedClasses {
  const tokens = str.trim().split(/\s+/);

  const buckets: BucketedClasses = {
    padding: [],
    margin: [],
    size: [],
    flexGrid: [],
    layout: [],
    typography: [],
    color: [],
    border: [],
    effects: [],
    animate: [],
    spacing: [],
    divide: [],
    others: [],
  };

  /** Helper: strip everything up to the last `:` so `hover:md:px-4` → `px-4` */
  const core = (tok: string) => tok.slice(tok.lastIndexOf(":") + 1);

  /** Ordered list of [bucket, matcher] pairs keeps evaluation cheap & readable */
  const rules: [keyof BucketedClasses, RegExp][] = [
    ["padding", /^p[trblxy]?-.+/],
    ["margin", /^-?m[trblxy]?-.+/],
    ["size", /^(min-|max-)?[wh]-/],
    /** ---------- new buckets ---------- */
    ["spacing", /^space-[xy]-|^space-[xy]-reverse$/],
    ["divide", /^divide-/],
    /** ---------- flex vs layout split ---------- */
    [
      "flexGrid",
      /^(grid|gap|place-|justify-|items-|content-|flex$|flex-(row|row-reverse|col|col-reverse|wrap|wrap-reverse))/,
    ],
    [
      "layout",
      /^(block|inline(|-block|-flex)|table|hidden|overflow-|static|fixed|absolute|relative|sticky|top-|bottom-|left-|right-|inset-|z-|flex-(?:none|auto|initial|\d+|\[[^\]]+\])|grow-|shrink-|basis-)/,
    ],
    [
      "typography",
      /^(text-|font-|leading-|tracking-|truncate|text-ellipsis|whitespace-|list-|decoration-|underline|line-through)/,
    ],
    ["color", /^(bg-|text-|from-|via-|to-|border-|ring-|fill-|stroke-)/],
    ["border", /^(border|rounded)(-|$)/],
    [
      "effects",
      /^(shadow-|opacity-|mix-blend-|backdrop-|filter|blur-|brightness-|contrast-|invert-|grayscale-|sepia-|hue-rotate-)/,
    ],
    ["animate", /^(transition|duration-|ease-|delay-|animate-|cursor-|select-|pointer-events-)/],
  ];

  tokens.forEach((tok) => {
    const naked = core(tok);
    const matched = rules.find(([_, rx]) => rx.test(naked));
    if (matched) buckets[matched[0]].push(tok);
    else buckets.others.push(tok);
  });

  return buckets;
}

export function siftClassNames(
  className: string | undefined,
  siftBy: PossiblyReadonlyArray<keyof BucketedClasses>,
): [string | undefined, string | undefined] {
  if (!isPresent(className)) return [undefined, undefined];

  const buckets = bucketTailwindClasses(className);
  const [matchingBuckets, nonMatchingBuckets] = sift(Object.entries(buckets), ([bucketName]) =>
    [siftBy].flat().includes(bucketName),
  );

  return [
    presence(
      matchingBuckets
        .map(([bucket, classNames]) => classNames)
        .flat()
        .join(" "),
    ),
    presence(
      nonMatchingBuckets
        .map(([bucket, classNames]) => classNames)
        .flat()
        .join(" "),
    ),
  ];
}
